import { useState, useEffect, useMemo, Fragment } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Header from "../components/layout/Header";
import BottomNavBar from "../components/layout/BottomNav";
import ProgramProgressCard from "../components/dashboard/ProgramProgressCard";
import { TrashIcon } from "@heroicons/react/24/outline";
import {
  HiCalendarDays,
  HiPlus,
  HiTrophy,
} from "react-icons/hi2";
import {
  Dialog,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useAuth } from "../contexts/AuthContext";
import { sessionService } from "../services/sessionService";
import { programService } from "../services/programService";
import { activityService } from "../services/activityService";
import { GlobalActivityService } from "../services/globalActivityService";
import { trainingTypeService } from "../services/trainingTypeService";
import { asDisplayNameOrNull } from "../utils/displayName";
import {
  ActivityType,
  TrainingType,
  GlobalActivityType,
} from "../types/activity";
import { Session, ActivityMeasurement, Program } from "../types/post";
import { PlanSessionModal } from "../components/sessions";
import SessionBottomSheet from "../components/sessions/SessionBottomSheet";
import EditSessionModal, {
  buildDateChangeUpdates,
} from "../components/sessions/EditSessionModal";
import { canChangeSessionDate } from "../utils/sessionRules";
import {
  ViewTabs,
  ListView,
  WeekView,
  MonthView,
  DayDetail,
  YearView,
  SessionVM,
  ScheduleView,
  getLocalDateString,
  getStartOfWeek,
  addDays,
  EventBottomSheet,
  EventCard,
  EventEditSheet,
  EventVM,
  buildEventVMs,
  buildWithdrawnEventVMs,
  eventsByDateKey,
  logDateForEvent,
} from "../components/schedule";
import { PeakEvent, PeakEventInput } from "../types/event";
import { invalidateUserEvents } from "../hooks/useUserEvents";
import {
  createEvent,
  deleteEvent,
  getAllEvents,
  getLoggedEventIds,
  updateEvent,
} from "../services/eventService";
import { TEXT_BUTTON_CLASS } from "../utils/buttonStyles";
import {
  getScheduledRecoveries,
  linkScheduledRecovery,
} from "../services/scheduledRecoveryService";
import {
  ScheduledRecovery,
  isScheduledRecoveryLogged,
} from "../types/scheduledRecovery";
import AddRecoveryLogForm from "../components/DayPost/AddRecoveryLogForm";
import {
  addLogIDToDayPost,
  addRecoveryLog,
} from "../services/dayPostService";
import { collectTagSuggestions } from "../utils/eventTagUtils";

const SESSIONS_PER_PAGE = 10;

// Plan 3d: load a date window instead of the whole collection. Today minus
// 4 weeks covers recent "To log" sessions; plus 12 weeks covers a full
// program. "Load earlier" extends the start by another 4 weeks at a time.
const WINDOW_WEEKS_BACK = 4;
const WINDOW_WEEKS_AHEAD = 12;
const LOAD_EARLIER_WEEKS = 4;

const initialRange = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return {
    start: getLocalDateString(addDays(today, -WINDOW_WEEKS_BACK * 7)),
    end: getLocalDateString(addDays(today, WINDOW_WEEKS_AHEAD * 7)),
  };
};

const mergeSessionsById = (
  ...lists: (Session & { id: string })[][]
): (Session & { id: string })[] => {
  const byId = new Map<string, Session & { id: string }>();
  for (const list of lists) for (const s of list) byId.set(s.id, s);
  return Array.from(byId.values());
};

// A denied or failed recovery read returns undefined so callers can still
// publish the sessions they already loaded.
const loadScheduledRecoveries = async (
  uid: string,
  start: string,
  end: string,
): Promise<ScheduledRecovery[] | undefined> => {
  try {
    return await getScheduledRecoveries(uid, start, end);
  } catch (error) {
    console.error("Error fetching scheduled recoveries:", error);
    return undefined;
  }
};

const formatRangeStart = (dateStr: string): string =>
  new Date(dateStr + "T00:00:00").toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });

const Schedule = () => {
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();

  // ─── Data state ────────────────────────────────────────────────────
  const [scheduledRecoveries, setScheduledRecoveries] = useState<
    ScheduledRecovery[]
  >([]);
  const [recoveryToLog, setRecoveryToLog] = useState<ScheduledRecovery | null>(
    null,
  );
  const [scheduledSessions, setScheduledSessions] = useState<
    (Session & { id: string })[]
  >([]);
  const [trainingTypes, setTrainingTypes] = useState<TrainingType[]>([]);
  const [activityTypes, setActivityTypes] = useState<ActivityType[]>([]);
  const [globalActivityTypes, setGlobalActivityTypes] = useState<
    Record<string, GlobalActivityType[]>
  >({});
  const [programs, setPrograms] = useState<(Program & { id: string })[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedRange, setLoadedRange] = useState(initialRange);
  const [loadingEarlier, setLoadingEarlier] = useState(false);

  // ─── Session sheet + date change (plan 3b/3c reused on the schedule) ──
  const [sheetSession, setSheetSession] = useState<
    (Session & { id: string }) | null
  >(null);
  const [dateChangeSession, setDateChangeSession] = useState<
    (Session & { id: string }) | null
  >(null);

  // ─── Events (Events.md §4) ─────────────────────────────────────────
  const [events, setEvents] = useState<PeakEvent[]>([]);
  const eventVMs = useMemo(() => buildEventVMs(events), [events]);
  const withdrawnEventVMs = useMemo(
    () => buildWithdrawnEventVMs(events),
    [events],
  );
  // Tag suggestions: tags on the athlete's own and coach-issued events.
  const eventTagSuggestions = useMemo(
    () => collectTagSuggestions(events),
    [events],
  );
  const eventsByDay = useMemo(() => eventsByDateKey(eventVMs), [eventVMs]);
  const getEventsForDate = (date: Date): EventVM[] =>
    eventsByDay.get(getLocalDateString(date)) || [];
  const [sheetEvent, setSheetEvent] = useState<EventVM | null>(null);
  const [editEvent, setEditEvent] = useState<PeakEvent | null>(null);
  const [isEventEditorOpen, setIsEventEditorOpen] = useState(false);
  // Events referenced by any training log → "Logged" (derived).
  const [loggedEventIds, setLoggedEventIds] = useState<Set<string>>(new Set());

  const refreshEvents = async () => {
    if (!currentUser?.uid) return;
    try {
      invalidateUserEvents(currentUser.uid);
      const list = await getAllEvents(currentUser.uid);
      setEvents(list);
      setLoggedEventIds(await getLoggedEventIds(currentUser.uid, list));
    } catch (error) {
      console.error("Error loading events:", error);
    }
  };
  useEffect(() => {
    refreshEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid]);
  const isEventLogged = (id: string) => loggedEventIds.has(id);

  const openEventEditor = (event: PeakEvent | null) => {
    setEditEvent(event);
    setIsEventEditorOpen(true);
  };

  const handleSaveEvent = async (input: PeakEventInput) => {
    if (!currentUser?.uid) return;
    if (editEvent) {
      // Coach-issued events are read-only for athletes.
      if (editEvent.coachIssued) return;
      await updateEvent(currentUser.uid, editEvent.id, input);
    } else {
      await createEvent(currentUser.uid, input);
    }
    await refreshEvents();
  };

  const handleWithdrawEvent = async (event: PeakEvent, withdrawn: boolean) => {
    if (!currentUser?.uid) return;
    await updateEvent(currentUser.uid, event.id, {
      status: withdrawn ? "withdrawn" : undefined,
    });
    await refreshEvents();
  };

  const handleDeleteEvent = async (event: PeakEvent) => {
    if (!currentUser?.uid) return;
    await deleteEvent(currentUser.uid, event.id);
    await refreshEvents();
    // Linked sessions had their eventId cleared.
    await refreshSessions();
  };

  // ─── View state ────────────────────────────────────────────────────
  const [searchParams, setSearchParams] = useSearchParams();
  const [scheduledView, setScheduledViewState] = useState<ScheduleView>(() => {
    const viewParam = searchParams.get("view");
    if (
      viewParam === "weekly" ||
      viewParam === "monthly" ||
      viewParam === "list" ||
      viewParam === "year"
    ) {
      return viewParam;
    }
    return "list";
  });
  const setScheduledView = (view: ScheduleView) => {
    setScheduledViewState(view);
    setSearchParams({ view }, { replace: true });
  };

  const [selectedWeekStart, setSelectedWeekStart] = useState<Date>(() =>
    getStartOfWeek(new Date()),
  );
  const [selectedMonth, setSelectedMonth] = useState<Date>(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [selectedYear, setSelectedYear] = useState<number>(() =>
    new Date().getFullYear(),
  );
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);

  // ─── Modal state ───────────────────────────────────────────────────
  const [isPlanSessionModalOpen, setIsPlanSessionModalOpen] = useState(false);
  const [planSessionInitialPlannedDate, setPlanSessionInitialPlannedDate] =
    useState<string | null>(null);

  // ─── Bulk selection + delete state ─────────────────────────────────
  const [isBulkSelectMode, setIsBulkSelectMode] = useState(false);
  const [selectedSessionIds, setSelectedSessionIds] = useState<Set<string>>(
    new Set(),
  );
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<string | null>(null);
  const [isDeletingSingle, setIsDeletingSingle] = useState(false);

  // ─── List pagination ("Load more") ─────────────────────────────────
  const [visibleCount, setVisibleCount] = useState(SESSIONS_PER_PAGE);

  // ─── Derived: inactive program ids ─────────────────────────────────
  const inactiveProgramIds = useMemo(() => {
    const set = new Set<string>();
    for (const p of programs) if (!p.isActive) set.add(p.id);
    return set;
  }, [programs]);

  // ─── Derived: upcoming sessions (hide rejected / inactive-program) ──
  const upcomingScheduledSessions = useMemo(() => {
    return scheduledSessions.filter((session) => {
      if (session.status === "rejected") return false;
      if (
        session.programLinkage?.programId &&
        inactiveProgramIds.has(session.programLinkage.programId)
      ) {
        return false;
      }
      if (session.status === "executing") return true;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const sessionDate = new Date(session.plannedDate + "T00:00:00");
      sessionDate.setHours(0, 0, 0, 0);
      return sessionDate >= today;
    });
  }, [scheduledSessions, inactiveProgramIds]);

  // ─── Name resolution helpers ───────────────────────────────────────
  const resolveSessionTypeName = (session: {
    trainingTypeID?: string;
    sessionTypeID?: string;
  }): string | null => {
    const resolved = trainingTypeService.getSessionTrainingTypeName(
      session,
      trainingTypes,
    );
    if (resolved) return resolved;
    const trimmed = session.sessionTypeID?.trim();
    if (trimmed?.includes(":")) return trimmed.split(":")[1] || null;
    return null;
  };

  const getRawActivityId = (activityTypeId: string): string => {
    if (activityTypeId.startsWith("global_")) {
      const parts = activityTypeId.split("_");
      if (parts.length >= 3) return parts.slice(2).join("_");
    }
    return activityTypeId;
  };

  const getGlobalCategoryIdFromActivityTypeId = (
    activityTypeId: string,
  ): string | undefined => {
    if (!activityTypeId.startsWith("global_")) return undefined;
    const parts = activityTypeId.split("_");
    if (parts.length >= 3) return parts[1];
    return undefined;
  };

  const getActivityName = (measurement: ActivityMeasurement): string => {
    const rawActivityId = getRawActivityId(measurement.ActivityTypeID);
    const userActivity = activityTypes.find((t) => t.id === rawActivityId);
    if (userActivity) return userActivity.name;
    const categoryId =
      measurement.globalCategoryID ||
      getGlobalCategoryIdFromActivityTypeId(measurement.ActivityTypeID);
    if (categoryId) {
      const categoryActivities = globalActivityTypes[categoryId];
      const globalActivity = categoryActivities?.find(
        (t) => t.id === rawActivityId,
      );
      if (globalActivity) return globalActivity.name;
    }
    for (const categoryActivities of Object.values(globalActivityTypes)) {
      const globalActivity = categoryActivities.find(
        (t) => t.id === rawActivityId,
      );
      if (globalActivity) return globalActivity.name;
    }
    return "Unknown Activity";
  };

  const getFirstActivityName = (
    session: Session & { id: string },
  ): string | null => {
    if (!session.ActivityMeasurementList?.length) return null;
    const firstName = getActivityName(session.ActivityMeasurementList[0]);
    return firstName !== "Unknown Activity" ? firstName : null;
  };

  // ─── Build session view-models ─────────────────────────────────────
  const toVM = (session: Session & { id: string }): SessionVM => {
    const typeName = resolveSessionTypeName(session);
    const title =
      asDisplayNameOrNull(session.name) ||
      typeName ||
      getFirstActivityName(session) ||
      "Session";
    const activityList = session.ActivityMeasurementList || [];
    const timeLabel = session.startTime
      ? new Date(session.startTime).toLocaleTimeString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        })
      : null;
    return {
      id: session.id,
      title,
      typeName,
      typeColorKey: session.trainingTypeID || typeName || null,
      exerciseCount: activityList.length,
      completedCount: activityList.filter((m) => m.timeLogged).length,
      linkedTrainingLogID: session.linkedTrainingLogID || null,
      plannedDate: session.plannedDate,
      timeLabel,
      status: session.status,
      isLive: session.status === "executing",
      programName: session.programLinkage?.programName,
      description: session.description,
      coachName: session.coach?.name,
      coachImageUrl: session.coach?.imageUrl,
      coachIssued: session.coachIssued,
      ...(session.eventId
        ? {
            eventId: session.eventId,
            eventName: events.find((e) => e.id === session.eventId)?.name,
          }
        : {}),
    };
  };

  const sessionVMsByDate = useMemo(() => {
    const map = new Map<string, SessionVM[]>();
    for (const s of upcomingScheduledSessions) {
      const vm = toVM(s);
      const list = map.get(s.plannedDate) || [];
      list.push(vm);
      map.set(s.plannedDate, list);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingScheduledSessions, trainingTypes, activityTypes, globalActivityTypes, events]);

  const listSessionVMs = useMemo(() => {
    return upcomingScheduledSessions
      .filter(
        (s) =>
          s.status === "proposed" ||
          s.status === "planned" ||
          s.status === "complete" ||
          s.status === "executing",
      )
      .sort((a, b) => a.plannedDate.localeCompare(b.plannedDate))
      .map(toVM);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upcomingScheduledSessions, trainingTypes, activityTypes, globalActivityTypes, events]);

  const getSessionsForDate = (date: Date): SessionVM[] =>
    sessionVMsByDate.get(getLocalDateString(date)) || [];

  const getRecoveriesForDate = (date: Date): ScheduledRecovery[] =>
    scheduledRecoveries.filter(
      (item) => item.plannedDate === getLocalDateString(date),
    );

  const openRecoveryLog = (item: ScheduledRecovery) => {
    if (isScheduledRecoveryLogged(item)) return;
    setRecoveryToLog(item);
  };

  const handleRecoverySubmit = async (recoveryLog: {
    selectedRecovery: string[];
    notes: string;
    pointsAmount: number;
    logDate?: string;
  }) => {
    if (!currentUser?.uid || !recoveryToLog) return;
    const dateID = (recoveryLog.logDate || recoveryToLog.plannedDate).replace(
      /-/g,
      "",
    );
    const { logDate: _logDate, ...recoveryData } = recoveryLog;
    const recoveryLogId = await addRecoveryLog(
      currentUser.uid,
      recoveryData,
      dateID,
    );
    await addLogIDToDayPost(
      currentUser.uid,
      dateID,
      recoveryLogId,
      "recoveryLogIDs",
    );
    await linkScheduledRecovery(currentUser.uid, recoveryToLog.id, recoveryLogId);
    setScheduledRecoveries((prev) =>
      prev.map((item) =>
        item.id === recoveryToLog.id
          ? { ...item, linkedRecoveryLogID: recoveryLogId }
          : item,
      ),
    );
    setRecoveryToLog(null);
  };

  // ─── Periodisation map for the Year view ───────────────────────────
  const periodisationByDate = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of programs) {
      if (!p.isActive || !p.startDate) continue;
      const start = new Date(p.startDate + "T00:00:00");
      for (const week of p.weeks || []) {
        if (!week.periodisation) continue;
        for (let d = 0; d < 7; d++) {
          const date = addDays(start, (week.weekNumber - 1) * 7 + d);
          map.set(getLocalDateString(date), week.periodisation);
        }
      }
    }
    return map;
  }, [programs]);

  const getPhaseForDate = (date: Date): string | undefined =>
    periodisationByDate.get(getLocalDateString(date));

  const phasesPresentInYear = useMemo(() => {
    const set = new Set<string>();
    periodisationByDate.forEach((phase, dateStr) => {
      if (dateStr.startsWith(String(selectedYear))) set.add(phase);
    });
    return Array.from(set).sort();
  }, [periodisationByDate, selectedYear]);

  // ─── Fetch data ────────────────────────────────────────────────────
  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.uid) return;
      try {
        setLoading(true);
        const range = initialRange();
        setLoadedRange(range);
        // The recovery query is separate from sessions. If it is denied or
        // fails, the training schedule still loads.
        const recoveryPromise = loadScheduledRecoveries(
          currentUser.uid,
          range.start,
          range.end,
        );
        const [sessionsData, userActivities, programsData] = await Promise.all(
          [
            sessionService.getSessionsForDateRange(
              currentUser.uid,
              range.start,
              range.end,
            ),
            activityService.fetchActivityTypes(currentUser.uid),
            programService.getAllPrograms(currentUser.uid),
          ],
        );
        const recoveryData = await recoveryPromise;
        setScheduledSessions(sessionsData);
        setScheduledRecoveries(recoveryData ?? []);
        setActivityTypes(userActivities);
        setPrograms(programsData);

        try {
          const types = await trainingTypeService.fetchTrainingTypes();
          setTrainingTypes(types);
        } catch (typesError) {
          console.error("Error fetching training types:", typesError);
        }

        const packIds = userData?.adminActivityCatIDs || [];
        if (packIds.length > 0) {
          const globalTypesMap: Record<string, GlobalActivityType[]> = {};
          await Promise.all(
            packIds.map(async (categoryId: string) => {
              const types =
                await GlobalActivityService.fetchActivityTypes(categoryId);
              globalTypesMap[categoryId] = types;
            }),
          );
          setGlobalActivityTypes(globalTypesMap);
        }
      } catch (error) {
        console.error("Error fetching schedule data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser?.uid, userData?.adminActivityCatIDs]);

  const refreshSessions = async () => {
    if (!currentUser?.uid) return;
    try {
      const sessionsData = await sessionService.getSessionsForDateRange(
        currentUser.uid,
        loadedRange.start,
        loadedRange.end,
      );
      setScheduledSessions(sessionsData);
    } catch (error) {
      console.error("Error refreshing sessions:", error);
    }
  };

  // "Load earlier": extend the window back by another chunk and merge.
  const handleLoadEarlier = async () => {
    if (!currentUser?.uid || loadingEarlier) return;
    const newStart = getLocalDateString(
      addDays(
        new Date(loadedRange.start + "T00:00:00"),
        -LOAD_EARLIER_WEEKS * 7,
      ),
    );
    const chunkEnd = getLocalDateString(
      addDays(new Date(loadedRange.start + "T00:00:00"), -1),
    );
    setLoadingEarlier(true);
    try {
      const recoveryPromise = loadScheduledRecoveries(
        currentUser.uid,
        newStart,
        chunkEnd,
      );
      const earlier = await sessionService.getSessionsForDateRange(
        currentUser.uid,
        newStart,
        chunkEnd,
      );
      const earlierRecoveries = await recoveryPromise;
      setScheduledSessions((prev) => mergeSessionsById(prev, earlier));
      if (earlierRecoveries !== undefined) {
        setScheduledRecoveries((prev) => {
          const seen = new Set(prev.map((item) => item.id));
          return [
            ...earlierRecoveries.filter((item) => !seen.has(item.id)),
            ...prev,
          ];
        });
      }
      setLoadedRange((prev) => ({ ...prev, start: newStart }));
    } catch (error) {
      console.error("Error loading earlier sessions:", error);
    } finally {
      setLoadingEarlier(false);
    }
  };

  // ─── Session actions ───────────────────────────────────────────────
  const canChangeDate = (sessionId: string): boolean => {
    const session = scheduledSessions.find((s) => s.id === sessionId);
    return canChangeSessionDate(session);
  };

  /**
   * Every reschedule on this page (week drag-and-drop, long-press, the
   * sheet, list and day-detail "Change date") ends here: optimistic update,
   * `sessionService.updateSession` (which also patches program copies and
   * day posts), rollback on failure. Eligibility follows decision 2.
   */
  const handleRescheduleSession = async (
    sessionId: string,
    newPlannedDate: string,
  ) => {
    if (!currentUser?.uid) return;
    const session = scheduledSessions.find((s) => s.id === sessionId);
    if (!session || !canChangeSessionDate(session)) return;
    const oldPlannedDate = session.plannedDate;
    if (!newPlannedDate || oldPlannedDate === newPlannedDate) return;

    const updates = buildDateChangeUpdates(session, newPlannedDate);
    setScheduledSessions((prev) =>
      prev.map((s) => (s.id === sessionId ? { ...s, ...updates } : s)),
    );
    try {
      await sessionService.updateSession(currentUser.uid, sessionId, updates);
    } catch (error) {
      console.error("Error rescheduling session:", error);
      setScheduledSessions((prev) =>
        prev.map((s) => (s.id === sessionId ? session : s)),
      );
      throw error;
    }
  };

  const openSheet = (sessionId: string) => {
    const session = scheduledSessions.find((s) => s.id === sessionId);
    if (session) setSheetSession(session);
  };

  const openChangeDate = (sessionId: string) => {
    const session = scheduledSessions.find((s) => s.id === sessionId);
    if (!session || !canChangeSessionDate(session)) return;
    setSheetSession(null);
    setDateChangeSession(session);
  };

  // Delete the athlete's own session from the sheet; the service unlinks it
  // from any training log and keeps the log.
  const handleDeleteFromSheet = async (session: Session & { id: string }) => {
    if (!currentUser?.uid) return;
    await sessionService.deleteSession(currentUser.uid, session.id);
    setScheduledSessions((prev) => prev.filter((s) => s.id !== session.id));
  };

  const handleRejectFromSheet = async (
    session: Session & { id: string },
    reason: string,
  ) => {
    if (!currentUser?.uid) return;
    await sessionService.rejectProposedSession(
      currentUser.uid,
      session.id,
      reason,
    );
    setScheduledSessions((prev) =>
      prev.map((s) =>
        s.id === session.id ? { ...s, status: "rejected" as const } : s,
      ),
    );
  };

  const openPlanSessionForDate = (date: Date) => {
    const target = new Date(date);
    target.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const effectiveDate = target < today ? today : target;
    setPlanSessionInitialPlannedDate(getLocalDateString(effectiveDate));
    setIsPlanSessionModalOpen(true);
  };

  const handlePlanSessionClick = () => openPlanSessionForDate(new Date());

  // ─── Bulk selection ────────────────────────────────────────────────
  const toggleSessionSelection = (sessionId: string) => {
    setSelectedSessionIds((prev) => {
      const next = new Set(prev);
      if (next.has(sessionId)) next.delete(sessionId);
      else next.add(sessionId);
      return next;
    });
  };

  const exitBulkSelectMode = () => {
    setIsBulkSelectMode(false);
    setSelectedSessionIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (!currentUser?.uid || selectedSessionIds.size === 0) return;
    setIsDeleting(true);
    try {
      await Promise.all(
        Array.from(selectedSessionIds).map((id) =>
          sessionService.deleteSession(currentUser.uid, id),
        ),
      );
      await refreshSessions();
      setShowDeleteConfirmation(false);
      exitBulkSelectMode();
    } catch (error) {
      console.error("Error deleting sessions:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteSingleSession = async () => {
    if (!currentUser?.uid || !sessionToDelete) return;
    setIsDeletingSingle(true);
    try {
      await sessionService.deleteSession(currentUser.uid, sessionToDelete);
      await refreshSessions();
      setSessionToDelete(null);
    } catch (error) {
      console.error("Error deleting session:", error);
    } finally {
      setIsDeletingSingle(false);
    }
  };

  const trainingTypeColors = userData?.trainingTypeColors;
  const periodisationColors = userData?.periodisationColors;

  const visibleListSessions = listSessionVMs.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-base-200">
      <Header />
      <main className="max-w-3xl mx-auto py-6 pb-32 sm:px-0 px-6">
        {/* Title + add menu */}
        <div className="flex items-center justify-between mb-4 pt-16">
          <h1 className="text-3xl md:text-4xl font-bold text-base-content font-heading">
            Schedule
          </h1>
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Add menu: session (plan flow) or event (event create sheet) */}
            <Menu as="div" className="relative flex-shrink-0">
              <MenuButton
                className="flex h-11 w-11 items-center justify-center rounded-full bg-primary text-primary-content hover:bg-primary/90 transition-colors"
                aria-label="Add session or event"
              >
                <HiPlus className="w-5 h-5" />
              </MenuButton>
              <MenuItems
                transition
                anchor={{ to: "bottom end", gap: 8 }}
                className="z-50 w-48 origin-top-right rounded-xl border border-base-300 bg-base-100 py-1 shadow-lg focus:outline-none transition data-closed:scale-95 data-closed:opacity-0 data-enter:duration-100 data-enter:ease-out data-leave:duration-75 data-leave:ease-in"
              >
                <MenuItem>
                  <button
                    type="button"
                    onClick={handlePlanSessionClick}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-grotesk text-base-content/80 data-focus:bg-base-200 focus:outline-none"
                  >
                    <HiCalendarDays className="w-4 h-4 flex-shrink-0" />
                    Add session
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    type="button"
                    onClick={() => openEventEditor(null)}
                    className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-grotesk text-base-content/80 data-focus:bg-base-200 focus:outline-none"
                  >
                    <HiTrophy className="w-4 h-4 flex-shrink-0" />
                    Add event
                  </button>
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>

        {/* Active program (plan 3g) — above the view switcher */}
        {currentUser?.uid && (
          <ProgramProgressCard userId={currentUser.uid} className="mb-4" />
        )}

        {/* View tabs */}
        <div className="mb-5">
          <ViewTabs value={scheduledView} onChange={setScheduledView} />
        </div>

        {/* Content */}
        {loading ? (
          <div className="p-8 sm:p-12 text-center">
            <h1 className="text-base-content text-[12rem] font-groteskLogo animate-pulse-custom font-light relative z-10">
              ^
            </h1>
            <p className="text-base-content/60 font-grotesk -mt-24">
              Loading scheduled sessions...
            </p>
          </div>
        ) : scheduledView === "list" ? (
          <ListView
            sessions={visibleListSessions}
            events={eventVMs}
            recoveries={scheduledRecoveries}
            onSelectRecovery={openRecoveryLog}
            isEventLogged={isEventLogged}
            onSelectEvent={(id) =>
              setSheetEvent(eventVMs.find((c) => c.id === id) ?? null)
            }
            trainingTypeColors={trainingTypeColors}
            hasMore={visibleCount < listSessionVMs.length}
            onLoadMore={() => setVisibleCount((c) => c + SESSIONS_PER_PAGE)}
            onSelectSession={openSheet}
            onDeleteSession={(id) => setSessionToDelete(id)}
            canChangeDate={canChangeDate}
            onChangeDate={openChangeDate}
            onPlanSession={handlePlanSessionClick}
            bulkSelectMode={isBulkSelectMode}
            onToggleBulkSelect={() =>
              isBulkSelectMode ? exitBulkSelectMode() : setIsBulkSelectMode(true)
            }
            selectedIds={selectedSessionIds}
            onToggleSelect={toggleSessionSelection}
            onDeleteSelected={() => setShowDeleteConfirmation(true)}
          />
        ) : scheduledView === "weekly" ? (
          <WeekView
            weekStart={selectedWeekStart}
            getSessionsForDate={getSessionsForDate}
            getEventsForDate={getEventsForDate}
            getRecoveriesForDate={getRecoveriesForDate}
            onSelectRecovery={openRecoveryLog}
            isEventLogged={isEventLogged}
            onSelectEvent={(id) =>
              setSheetEvent(eventVMs.find((c) => c.id === id) ?? null)
            }
            trainingTypeColors={trainingTypeColors}
            onPrevWeek={() =>
              setSelectedWeekStart((prev) => addDays(prev, -7))
            }
            onNextWeek={() => setSelectedWeekStart((prev) => addDays(prev, 7))}
            onToday={() => setSelectedWeekStart(getStartOfWeek(new Date()))}
            onAssign={(date) => openPlanSessionForDate(date)}
            onSelectSession={openSheet}
            onReschedule={(id, date) => {
              handleRescheduleSession(id, date).catch(() => undefined);
            }}
            canChangeDate={canChangeDate}
            onChangeDate={openChangeDate}
          />
        ) : scheduledView === "monthly" ? (
          <>
            <MonthView
              month={selectedMonth}
              selectedDay={selectedDay}
              getSessionsForDate={getSessionsForDate}
              getEventsForDate={getEventsForDate}
              getRecoveriesForDate={getRecoveriesForDate}
              trainingTypeColors={trainingTypeColors}
              onPrevMonth={() => {
                setSelectedDay(null);
                setSelectedMonth(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1),
                );
              }}
              onNextMonth={() => {
                setSelectedDay(null);
                setSelectedMonth(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1),
                );
              }}
              onSelectDay={(date) => setSelectedDay(date)}
            />
            {selectedDay && (
              <DayDetail
                date={selectedDay}
                sessions={getSessionsForDate(selectedDay)}
                events={getEventsForDate(selectedDay)}
                recoveries={getRecoveriesForDate(selectedDay)}
                onSelectRecovery={openRecoveryLog}
                isEventLogged={isEventLogged}
                onSelectEvent={(id) =>
                  setSheetEvent(eventVMs.find((c) => c.id === id) ?? null)
                }
                trainingTypeColors={trainingTypeColors}
                onAssign={() => openPlanSessionForDate(selectedDay)}
                onSelectSession={openSheet}
                onDeleteSession={(id) => setSessionToDelete(id)}
                canChangeDate={canChangeDate}
                onChangeDate={openChangeDate}
              />
            )}
          </>
        ) : (
          <YearView
            year={selectedYear}
            getPhaseForDate={getPhaseForDate}
            getEventsForDate={getEventsForDate}
            phasesPresent={phasesPresentInYear}
            periodisationColors={periodisationColors}
            onPrevYear={() => setSelectedYear((y) => y - 1)}
            onNextYear={() => setSelectedYear((y) => y + 1)}
            onSelectDay={(date) => {
              setSelectedMonth(new Date(date.getFullYear(), date.getMonth(), 1));
              setSelectedDay(date);
              setScheduledView("monthly");
            }}
          />
        )}

        {!loading && withdrawnEventVMs.length > 0 && (
          <section className="mt-8" aria-label="Withdrawn events">
            <h2 className="text-sm font-semibold text-base-content font-grotesk">
              Withdrawn
            </h2>
            <p className="mt-1 mb-3 text-sm text-base-content/60 font-grotesk">
              These events are off the calendar. Open one to reinstate or delete
              it.
            </p>
            <div className="space-y-2">
              {withdrawnEventVMs.map((vm) => (
                <EventCard
                  key={vm.id}
                  event={vm}
                  logged={isEventLogged(vm.id)}
                  onClick={() => setSheetEvent(vm)}
                />
              ))}
            </div>
          </section>
        )}

        {/* Loaded window + "Load earlier" (plan 3d). The list shows upcoming
            sessions only, so the control lives under the calendar views. */}
        {!loading &&
          (scheduledView === "weekly" || scheduledView === "monthly") && (
            <div className="mt-6 flex flex-col items-center gap-2 text-center">
              <span className="text-xs text-base-content/50 font-grotesk">
                Showing sessions from{" "}
                <span className="font-geist text-base-content/70">
                  {formatRangeStart(loadedRange.start)}
                </span>
              </span>
              <button
                type="button"
                onClick={handleLoadEarlier}
                disabled={loadingEarlier}
                className={TEXT_BUTTON_CLASS}
              >
                {loadingEarlier ? "Loading..." : "Load earlier"}
              </button>
            </div>
          )}
      </main>
      <BottomNavBar />

      {/* Session sheet (slice 9) reused on the schedule: Open, Link (via the
          day's dashboard), Change date, Delete / Reject. */}
      <SessionBottomSheet
        isOpen={!!sheetSession}
        onClose={() => setSheetSession(null)}
        session={sheetSession}
        title={
          sheetSession
            ? asDisplayNameOrNull(sheetSession.name) ||
              resolveSessionTypeName(sheetSession) ||
              "Session"
            : ""
        }
        typeName={sheetSession ? resolveSessionTypeName(sheetSession) : null}
        ownerUserId={currentUser?.uid}
        onOpenSession={(session) => {
          setSheetSession(null);
          navigate(`/session/${session.id}`);
        }}
        onLinkToLog={(session) => {
          setSheetSession(null);
          navigate(`/dashboard?date=${session.plannedDate.replace(/-/g, "")}`);
        }}
        onChangeDate={(session) => openChangeDate(session.id)}
        onDelete={handleDeleteFromSheet}
        onReject={handleRejectFromSheet}
      />

      {/* Event sheet (Events.md §4) */}
      <EventBottomSheet
        isOpen={!!sheetEvent}
        onClose={() => setSheetEvent(null)}
        event={sheetEvent}
        ownerUserId={currentUser?.uid}
        onLinksChanged={refreshEvents}
        onLogPerformance={(event) => {
          setSheetEvent(null);
          navigate(
            `/feed?date=${logDateForEvent(event)}&addLog=performance&eventId=${encodeURIComponent(event.id)}`,
          );
        }}
        onEdit={(event) => {
          setSheetEvent(null);
          openEventEditor(event);
        }}
      />
      <EventEditSheet
        isOpen={isEventEditorOpen}
        onClose={() => setIsEventEditorOpen(false)}
        event={editEvent}
        tagSuggestions={eventTagSuggestions}
        defaultDate={getLocalDateString(new Date()).replace(/-/g, "")}
        onSave={handleSaveEvent}
        onWithdraw={handleWithdrawEvent}
        onDelete={handleDeleteEvent}
      />
      {dateChangeSession && (
        <EditSessionModal
          isOpen={!!dateChangeSession}
          onClose={() => setDateChangeSession(null)}
          session={dateChangeSession}
          dateOnly
          onSaveDate={handleRescheduleSession}
          onSessionUpdated={() => setDateChangeSession(null)}
        />
      )}

      <PlanSessionModal
        isOpen={isPlanSessionModalOpen}
        onClose={() => {
          setIsPlanSessionModalOpen(false);
          setPlanSessionInitialPlannedDate(null);
        }}
        onSessionCreated={refreshSessions}
        initialPlannedDate={planSessionInitialPlannedDate}
      />

      {/* Bulk delete confirmation */}
      <Transition appear show={showDeleteConfirmation} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setShowDeleteConfirmation(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden rounded-2xl bg-base-100 shadow-xl transition-all">
                  <div className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="p-2 rounded-full bg-error/15">
                        <TrashIcon className="w-6 h-6 text-error" />
                      </div>
                      <Dialog.Title className="text-lg font-semibold text-base-content font-grotesk">
                        Delete Sessions
                      </Dialog.Title>
                    </div>
                    <p className="text-sm text-base-content/70 font-grotesk mb-6">
                      Are you sure you want to delete {selectedSessionIds.size}{" "}
                      session{selectedSessionIds.size !== 1 ? "s" : ""}? This
                      action cannot be undone.
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowDeleteConfirmation(false)}
                        disabled={isDeleting}
                        className={`${TEXT_BUTTON_CLASS} flex-1`}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleBulkDelete}
                        disabled={isDeleting}
                        className="btn btn-error flex-1 min-h-11 h-11 rounded-xl text-sm font-medium font-grotesk normal-case gap-2"
                      >
                        {isDeleting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            Deleting...
                          </>
                        ) : (
                          "Delete"
                        )}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Single session delete confirmation */}
      <Transition appear show={sessionToDelete !== null} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[60]"
          onClose={() => setSessionToDelete(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-sm rounded-2xl bg-base-100 p-6 shadow-xl">
                  <Dialog.Title className="text-lg font-semibold text-base-content font-heading mb-2">
                    Delete Session
                  </Dialog.Title>
                  <p className="text-sm text-base-content/70 font-grotesk mb-6">
                    Are you sure you want to delete this session? This action
                    cannot be undone.
                  </p>
                  <div className="flex gap-3">
                    <button
                      onClick={() => setSessionToDelete(null)}
                      disabled={isDeletingSingle}
                      className={`${TEXT_BUTTON_CLASS} flex-1`}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteSingleSession}
                      disabled={isDeletingSingle}
                      className="btn btn-error flex-1 min-h-11 h-11 rounded-xl text-sm font-medium font-grotesk normal-case gap-2"
                    >
                      {isDeletingSingle ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                          Deleting...
                        </>
                      ) : (
                        "Delete"
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
      <AddRecoveryLogForm
        open={recoveryToLog !== null}
        onClose={() => setRecoveryToLog(null)}
        onSubmit={handleRecoverySubmit}
        dateID={(recoveryToLog?.plannedDate || "").replace(/-/g, "")}
        initialData={
          recoveryToLog
            ? {
                selectedRecovery: [recoveryToLog.modality],
                notes: recoveryToLog.notes,
              }
            : undefined
        }
      />
    </div>
  );
};

export default Schedule;
